import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import login1 from "../assets/login1.jpg";
import login2 from "../assets/login2.jpg";
import login3 from "../assets/login3.jpg";
import login4 from "../assets/login4.jpg";

const SLIDES = [login1, login2, login3, login4];

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [slide, setSlide] = useState(0);
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % SLIDES.length), 2000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email, password, remember);
    setLoading(false);
    if (res.ok) navigate("/", { replace: true });
    else setError(res.error);
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden lg:block">
        {SLIDES.map((src, i) => (
          <img
            key={i}
            src={src}
            alt="GFM facilities"
            data-testid={`login-slide-${i}`}
            className="absolute inset-0 h-full w-full object-cover transition-opacity duration-700"
            style={{ opacity: slide === i ? 1 : 0 }}
          />
        ))}
        <div className="absolute inset-0" style={{ background: "rgba(0,53,94,0.82)" }} />
        <div className="absolute inset-0 flex flex-col justify-between p-12 text-white">
          <Logo variant="dark" showName={false} />
          <div>
            <h1 className="font-heading text-4xl font-extrabold leading-tight tracking-tight">
              GFM - Permit To Work Management System
            </h1>
            <p className="mt-4 max-w-sm text-white/70">
              Issue site inductions, control of access, general hazards and hot works permits. Capture contractor photos and print compliant A4 permits and ID badges.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center bg-background px-6 py-12">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="mb-8 lg:hidden">
            <Logo />
          </div>
          <div className="mb-1 text-xs font-semibold uppercase tracking-[0.2em] text-brand">Staff Sign In</div>
          <h2 className="mb-6 font-heading text-3xl font-bold tracking-tight text-foreground">Welcome back</h2>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input id="password" data-testid="login-password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className="pr-10" />
                <button type="button" onClick={() => setShowPw((s) => !s)} data-testid="toggle-password-visibility"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
              <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="remember-me" />
              Keep me signed in for 90 days
            </label>
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" data-testid="login-submit" className="w-full bg-brand hover:bg-brand-dark" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Sign in
            </Button>
          </form>
          <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-wider text-muted-foreground">
            <div className="h-px flex-1 bg-border" /> or <div className="h-px flex-1 bg-border" />
          </div>
          <Button type="button" variant="outline" className="w-full" data-testid="microsoft-login-btn"
            onClick={() => { window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/microsoft/login`; }}>
            <svg className="mr-2 h-4 w-4" viewBox="0 0 23 23" aria-hidden="true">
              <rect x="1" y="1" width="10" height="10" fill="#F25022" />
              <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
              <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
              <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
            </svg>
            Continue with Microsoft
          </Button>
          <p className="mt-6 text-sm text-muted-foreground">Staff sign in with Microsoft; new accounts need administrator approval. Admins can also use email &amp; password above.</p>
          <div className="mt-4 border-t border-border pt-4 text-center text-sm">
            <span className="text-muted-foreground">Are you a contractor?</span>{" "}
            <a href="/contractor/login" className="font-semibold text-brand hover:underline" data-testid="contractor-portal-link">Go to the contractor portal</a>
          </div>
        </div>
      </div>
    </div>
  );
}
